import jwtDecode from 'jwt-decode';
import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponseModel } from '../../@types/base';
import { User } from '../../@types/account';
import { CONSTS } from './const';
import { dispatch, store } from '../store';
import axios from '../../utils/axios';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import { Roles } from '../../constants/roles.enum';

// ----------------------------------------------------------------------

type AuthJWTState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User;
  roles: string[];
  isSuperAdmin: boolean;
  landingPath: string | null;
};

const unAuthUser: User = {
  id: '',
  email: '',
  companyId: -1,
  roles: [],
  isSuperior: false
};

const authUser: User = {
  id: '0',
  // userName: '',
  email: '',
  companyId: -1,
  roles: [],
  isSuperior: false
};

const initialState: AuthJWTState = {
  isLoading: false,
  isAuthenticated: false,
  user: unAuthUser,
  roles: [],
  isSuperAdmin: false,
  landingPath: null
};

const slice = createSlice({
  name: 'authJwt',
  initialState,
  reducers: {
    // START IS SUPER ADMIN
    setIsSuperAdmin(state) {
      state.isSuperAdmin = true;
    },

    setLandingPath(state, action) {
      state.landingPath = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
      state.roles = action.payload.roles;
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.roles = action.payload.roles;
      state.landingPath = action.payload.landingPath;
    },

    // SET SUPERIOR
    setSuperiorSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGIN ERROR
    setLoginError(state) {
      state.isAuthenticated = false;
      state.user = unAuthUser;
      state.roles = [];
      state.landingPath = null;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = authUser;
      state.roles = [];
      state.landingPath = null;
    }
  }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }
  const decoded = jwtDecode<{ exp: number }>(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (accessToken: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    // axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    axios.defaults.headers.common['x-access-token'] = accessToken;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common['x-access-token'];
  }
};

// ----------------------------------------------------------------------

export function setSuperAdmin() {
  return async () => {
    const { dispatch } = store;
    dispatch(slice.actions.setIsSuperAdmin());
  };
}

export async function getUserInfoFromToken(token: string) {
  const userInfo: userType = await jwtDecode(token);
  return userInfo || null;
}

export async function setUserLandingPath(path: string) {
  try {
    const res = await axios.post(CONSTS.USER_POST_SET_LANDING_PATH, {
      landingPath: path
    });
    if (res.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.setLandingPath(path));
      return true;
    }
    return false;
  } catch (error) {
    console.error('error:', error);
    return false;
  }
}


export async function login({
                              email,
                              password,
                              deviceToken
                            }: {
  email: string;
  password: string;
  deviceToken: string | null;
}) {
  const { dispatch } = store;

  try {

    const response: any = await axios
      .post(CONSTS.AUTH_POST_LOGIN, {
        email,
        password,
        deviceToken,
        deviceType: 'WEB'
      });
    // .then((response) => {
    //   console.log(response.data);
    //
    //   return response;
    // })
    // .catch((error) => {
    //   isError = true;
    //   console.log(error);
    //   return error && error.response ? error.response : error;
    // });

    if (
      response &&
      response.status === HttpStatusCodes.StatusSuccess &&
      response.data
    ) {
      const {
        id,
        email,
        roles,
        accessToken,
        companyId,
        dashboardLandingPath
      } = response.data.data;

      const userInfo = await getUserInfoFromToken(accessToken);

      setSession(accessToken);

      // localStorage.setItem("landingPath", JSON.stringify(dashboardLandingPath));

      const user: User = {
        id: id || authUser.id,
        // userName: username || '',
        email: email || '',
        roles: roles && roles.length >= 0 ? roles : [],
        companyId: companyId > 0 ? companyId : -1,
        isSuperior: false
        /* displayName: authUser.displayName,
        password: authUser.password,
        photoURL: authUser.photoURL,
        country: authUser.country,
        address: authUser.address,
        state: authUser.state,
        city: authUser.city,
        zipCode: authUser.zipCode,
        about: authUser.about,
        role,
        isPublic: authUser.isPublic,
        profileImageUrl: '',
        phoneNumber: ''
        adress: '',
        fullName: '' */
      };

      dispatch(
        slice.actions.loginSuccess({
          user,
          roles: userInfo ? userInfo.roles : '',
          landingPath: dashboardLandingPath
        })
      );
    }
    return response;
  } catch (error: any) {
    return error && error.response ? error.response : error;
  }
}

// ----------------------------------------------------------------------

export function register({
                           email,
                           password,
                           firstName,
                           lastName
                         }: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}) {
  return async () => {
    const { dispatch } = store;

    const response = await axios.post('', {
      email,
      password,
      firstName,
      lastName
    });
    const { token, user } = response.data;

    window.localStorage.setItem('accessToken', token);
    dispatch(slice.actions.registerSuccess({ user }));
  };
}

// ----------------------------------------------------------------------

export async function setAdminVirtualCompanyId(
  virtualCompanyId: number | null | string,
  accessToken?: string
) {
  try {
    if (accessToken) axios.defaults.headers.common['x-access-token'] = accessToken;
    const response: AxiosResponseModel<null> = await axios
      .post(CONSTS.ADMIN_POST_SET_VIRTUAL_COMPANY, {
        companyId: virtualCompanyId
      })
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    return response && response.status === HttpStatusCodes.StatusSuccess;

  } catch (error: any) {
    return false;
  }
}

export async function setUserToAdminWhenSuperior(user: User) {
  const { dispatch } = store;

  try {
    const roles = user.roles.filter((item) => item !== Roles.USER);
    roles.push(Roles.ADMIN);

    const userInfo: User = {
      id: user.id,
      email: user.email,
      roles,
      companyId: user.companyId,
      isSuperior: false
    };

    await dispatch(
      slice.actions.setSuperiorSuccess({
        user: userInfo
      })
    );
    return true;
  } catch (error) {
    console.error('error:', error);
    dispatch(
      slice.actions.setSuperiorSuccess({
        user
      })
    );
    return false;
  }
}

export async function setAdminToUserWhenSuperior(user: User) {
  const { dispatch } = store;

  try {
    // !HATA: buraya baklıacak, hata veriyor.
    const roles = user.roles.filter((item) => item !== Roles.ADMIN);
    roles.push(Roles.USER);

    const userInfo: User = {
      id: user.id,
      email: user.email,
      roles,
      companyId: user.companyId,
      isSuperior: true
    };

    await dispatch(
      slice.actions.setSuperiorSuccess({
        user: userInfo
      })
    );
    return true;
  } catch (error) {
    console.error('error:', error);
    dispatch(
      slice.actions.setSuperiorSuccess({
        user
      })
    );
    return false;
  }
}

// ----------------------------------------------------------------------

export function logout() {
  return async () => {
    const { dispatch } = store;

    await axios
      .post(CONSTS.AUTH_POST_LOGOUT)
      .then((response) => response)
      .catch((error) => error && error.response ? error.response : error);

    setSession(null);

    dispatch(slice.actions.logoutSuccess());
  };
}

// ----------------------------------------------------------------------
type userType = {
  activeLanguage: string;
  roles: string[];
  id: number;
  companyId: number;
  email: string;
  iat: number;
  exp: number;
};

export function getInitialize() {
  return async () => {
    const { dispatch } = store;

    // dispatch(slice.actions.startLoading());
    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        /* const response = await axios.get(''); */

        const userInfo: userType = await jwtDecode(accessToken);

        const isSuperior = localStorage.getItem('superior');

        const setUser: User = {
          id: authUser.id,
          // userName: userInfo.email ? response.data.username : '',
          email: userInfo ? userInfo.email : '',
          companyId: userInfo ? userInfo.companyId : -1,
          roles: userInfo ? userInfo.roles : [],
          isSuperior: isSuperior === '1'
          /* displayName: response.data
            ? response.data.fullName
            : authUser.displayName,
          password: authUser.password,
          photoURL: authUser.photoURL,
          country: authUser.country,
          address: authUser.address,
          state: authUser.state,
          city: authUser.city,
          zipCode: authUser.zipCode,
          about: authUser.about,
          role: userInfo ? userInfo.Roles : authUser.role,
          isPublic: authUser.isPublic,

          /* profileImageUrl: response.data
            ? response.data.profileImageUrl || authUser.photoURL
            : '', * /
          phoneNumber: response.data ? response.data.phoneNumber : ''
          adress: response.data ? response.data.address : '',
          fullName: response.data ? response.data.fullName : '' */
        };

        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            user: setUser,
            roles: userInfo.roles
          })
        );
      } else {
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: unAuthUser,
            roles: []
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
          rules: []
        })
      );
    }
  };
}
